import React from "react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/primitives";
import { Icon } from "@/components/icons";
import {
  ArrowDown,
  User,
  CreditCard,
  Wallet,
  Cpu,
  Bank,
  ChartLineUp,
  ArrowLineDown,
  WarningCircle,
} from "@phosphor-icons/react";

const FLOW = [
  { icon: User, label: "User", note: "Member initiates via dashboard" },
  { icon: CreditCard, label: "Payment / On-Ramp Provider", note: "Third-party payment infrastructure" },
  { icon: Wallet, label: "Supported Wallet / Trading Infrastructure", note: "Assets may be transferred to trading venues for execution" },
  { icon: Cpu, label: "Q-Core Execution Engine", note: "Analyzes markets & executes qualifying strategies" },
  { icon: Bank, label: "Supported Exchange / DEX", note: "CEX, DEX, and derivatives venues" },
  { icon: ChartLineUp, label: "Trade Activity", note: "Recorded & monitored" },
  { icon: ArrowLineDown, label: "Withdrawal Infrastructure", note: "Third-party withdrawal processing" },
  { icon: User, label: "User", note: "Funds return to member" },
];

export function ArchitectureDiagram() {
  return (
    <div className="relative mx-auto max-w-2xl" data-testid="architecture-diagram">
      {FLOW.map((step, i) => (
        <Reveal key={i} delay={i * 0.05}>
          <div className="flex items-center gap-4 rounded-lg border border-white/10 bg-[#0A0B10] px-5 py-4">
            <span className="grid place-items-center h-11 w-11 shrink-0 rounded-lg border border-[#2DD4BF]/30 bg-[#2DD4BF]/10 text-[#2DD4BF]">
              <step.icon size={22} weight="duotone" />
            </span>
            <div className="min-w-0">
              <p className="font-mono text-xs uppercase tracking-widest text-white">{step.label}</p>
              <p className="text-sm text-[#94A3B8] mt-0.5">{step.note}</p>
            </div>
          </div>
          {i < FLOW.length - 1 && (
            <div className="flex justify-center py-2">
              <ArrowDown size={18} className="text-[#0E9E90]" />
            </div>
          )}
        </Reveal>
      ))}
    </div>
  );
}

const RISK_FACTORS = [
  "Market volatility", "Slippage", "Liquidity constraints", "Network congestion",
  "Exchange outages", "Execution failures", "Smart-contract vulnerabilities",
  "Counterparty risk", "API failures", "Trading fees", "Blockchain fees", "Unexpected market conditions",
];

export function RiskSection() {
  return (
    <div data-testid="risk-section" className="rounded-2xl border border-amber-400/25 bg-amber-400/[0.04] p-8 md:p-12">
      <div className="flex items-center gap-3 mb-5">
        <span className="grid place-items-center h-11 w-11 rounded-lg border border-amber-400/40 bg-amber-400/10 text-amber-300">
          <WarningCircle size={24} weight="duotone" />
        </span>
        <h3 className="text-2xl md:text-3xl font-black text-white">Trading Involves Risk.</h3>
      </div>
      <p className="text-[#cbd5e1] max-w-3xl leading-relaxed">
        Automated trading does not eliminate market risk. Arbitrage opportunities can disappear quickly.
        Losses may occur. We believe in transparency over hype — understand the risks before you decide.
      </p>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {RISK_FACTORS.map((f) => (
          <div key={f} className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2.5">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" />
            <span className="text-sm text-[#cbd5e1]">{f}</span>
          </div>
        ))}
      </div>
      <p className="mt-8 font-mono text-xs text-amber-300/80 border-t border-amber-400/20 pt-5">
        We never use "guaranteed returns", "risk-free", "guaranteed profit", or "never lose money".
      </p>
    </div>
  );
}

export function TrustPrinciple() {
  const items = ["Understand the technology.", "Understand the architecture.", "Understand the risks.", "Verify what can be verified.", "Then decide."];
  return (
    <div className="relative rounded-2xl border border-white/10 bg-gradient-to-b from-[#0A0B10] to-[#08090F] p-8 md:p-14 overflow-hidden" data-testid="trust-principle">
      <div className="absolute inset-0 qs-grid opacity-20 pointer-events-none" aria-hidden="true" />
      <div className="relative">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#2DD4BF] mb-6">Core Trust Principle</p>
        <h3 className="text-3xl md:text-5xl font-black text-white leading-[1.1] max-w-3xl">
          We don't ask you to trust us blindly.
        </h3>
        <div className="mt-10 flex flex-wrap gap-3">
          {items.map((t, i) => (
            <motion.span
              key={t}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="rounded-full border border-[#2DD4BF]/25 bg-[#2DD4BF]/[0.06] px-5 py-2.5 text-sm text-white"
            >
              {t}
            </motion.span>
          ))}
        </div>
      </div>
    </div>
  );
}
